import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BodyText, SideDrawer, withStyles } from 'vopay';
import type { AccountUserRole } from 'vopay';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import useFetchRolePermissionsCatalog from '@/hooks/useApi/useFetchRolePermissionsCatalog';
import PermissionsSearchField from '../UserPermissions/PermissionsSearchField';
import styles from './RolePermissionsViewDrawer.module.scss';

const cn = withStyles(styles);

type RolePermissionsViewDrawerProps = {
  isOpen: boolean;
  role: AccountUserRole | null;
  onClose: () => void;
};

type GroupedPermissions = {
  groupName: string;
  items: { permissionKey: string; label: string }[];
}[];

/**
 * Stacked sub-drawer that lists the permissions a role has enabled, grouped
 * by category. Read-only — no form, no checkboxes. The catalog is fetched
 * via the cached hook so opening this drawer doesn't trigger a network call
 * after the first time.
 */
const RolePermissionsViewDrawer: FC<RolePermissionsViewDrawerProps> = ({
  isOpen,
  role,
  onClose,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UserRoles.permissionsViewDrawer' });
  const { response: loginResponse } = useClientLogin();
  const loggedInEmail = loginResponse?.LoginUserName;

  const [searchTerm, setSearchTerm] = useState('');
  const { catalog, fetching, fetchCatalog } = useFetchRolePermissionsCatalog();

  useEffect(() => {
    if (isOpen && loggedInEmail && Object.keys(catalog).length === 0) {
      fetchCatalog(loggedInEmail).catch(() => {
        // already logged in the hook
      });
    }
  }, [isOpen, loggedInEmail]);

  useEffect(() => {
    if (isOpen) setSearchTerm('');
  }, [isOpen]);

  const grouped: GroupedPermissions = useMemo(() => {
    if (!role || Object.keys(catalog).length === 0) return [];
    const owned = new Set(role.Permissions);
    const needle = searchTerm.trim().toLowerCase();

    const out: GroupedPermissions = [];
    Object.entries(catalog).forEach(([groupName, group]) => {
      const items = Object.entries(group)
        .filter(([permissionKey]) => owned.has(permissionKey))
        .map(([permissionKey, item]) => ({ permissionKey, label: item.label }))
        .filter(({ label }) => (needle ? label.toLowerCase().includes(needle) : true));

      if (items.length > 0) out.push({ groupName, items });
    });
    return out;
  }, [catalog, role, searchTerm]);

  const totalVisible = grouped.reduce((sum, g) => sum + g.items.length, 0);

  if (!role) return null;

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        <Box flx="col" gapSize="12px">
          <span>{t('title', { defaultValue: 'Permissions' })}</span>
          <PermissionsSearchField
            name="permissionsViewSearch"
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('searchPlaceholder', { defaultValue: 'Search Permissions' })}
          />
        </Box>
      }
    >
      <div className={cn('body')}>
        {fetching && (
          <BodyText weight="md">
            {t('loading', { defaultValue: 'Loading permissions…' })}
          </BodyText>
        )}

        {!fetching && totalVisible === 0 && (
          <BodyText weight="md" className={cn('emptyState')}>
            {searchTerm
              ? t('noMatches', { defaultValue: 'No permissions match your search.' })
              : t('noPermissions', { defaultValue: 'This role has no permissions enabled.' })}
          </BodyText>
        )}

        {!fetching &&
          grouped.map((group) => (
            <div key={group.groupName} className={cn('group')}>
              <h3 className={cn('groupHeading')}>{group.groupName}</h3>
              <ul className={cn('permissionList')}>
                {group.items.map((item) => (
                  <li key={item.permissionKey} className={cn('permissionItem')}>
                    {item.label}
                  </li>
                ))}
              </ul>
            </div>
          ))}
      </div>
    </SideDrawer>
  );
};

export default RolePermissionsViewDrawer;
